/**
 * =============================================================================
 * Componente: AffiliateInvitesTab
 * Versão: 2.0.0
 *
 * Descrição:
 *   Aba "Convites" do detalhe de um programa privado (invite_only). Permite
 *   criar novos convites, listar pendentes/aceitos/revogados, reenviar e
 *   revogar. Reaproveita infraestrutura de Resend/branding via a Edge Function
 *   send-affiliate-invite.
 *
 * CHANGELOG:
 *   v2.0.0 (2026-07-22) — Refatoração visual para o padrão `list-page` do
 *     sistema (mesmo layout usado em ReferralAffiliates embedded):
 *       • KPI cards (Total / Pendentes / Aceitos / Revogados+Expirados),
 *         calculados client-side a partir do payload SEM filtro de status
 *         (segunda chamada ao mesmo hook, cache separado — custo desprezível).
 *       • Filtros no wrapper padrão (`ListPageFiltersBar`) com Input e Select
 *         shadcn (adeus `<select>` nativo).
 *       • Tabela envolvida em `ListPageContent`, rodapé em `ListPageFooter`.
 *       • Mantém 100% das mutations e do dialog "Novo convite" pré-existentes.
 *   v1.0.0 — Versão inicial.
 * =============================================================================
 */
import { useMemo, useState } from 'react';
import {
  Mail, Plus, RefreshCw, Trash2, Search, Loader2, CheckCircle2,
  Clock, XCircle, AlertCircle, CircleDot,
} from 'lucide-react';
import {
  useAffiliateProgramInvites,
  useCreateAffiliateProgramInvite,
  useRevokeAffiliateProgramInvite,
  useResendAffiliateProgramInvite,
  type AffiliateProgramInvite,
} from '@/hooks/useAffiliateProgramInvites';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ListPageLayout,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
  ListPageContent,
  ListPageFooter,
} from '@/components/layout/list-page';
import { cn } from '@/lib/utils';
import { InlineNotice } from '@/components/ui/inline-notice';

interface Props {
  programId: string;
}

// Meta de status: apenas cor do ícone, texto sempre em foreground (preto).
const STATUS_META: Record<
  AffiliateProgramInvite['status'],
  { label: string; icon: typeof Clock; iconClass: string }
> = {
  pending:  { label: 'Pendente', icon: Clock,        iconClass: 'text-amber-500' },
  accepted: { label: 'Aceito',   icon: CheckCircle2, iconClass: 'text-emerald-500' },
  expired:  { label: 'Expirado', icon: AlertCircle,  iconClass: 'text-muted-foreground' },
  revoked:  { label: 'Revogado', icon: XCircle,      iconClass: 'text-red-500' },
};

export function AffiliateInvitesTab({ programId }: Props) {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('all');
  const [dialogOpen, setDialogOpen] = useState(false);

  // Listagem filtrada (respeita status escolhido)
  const { data: invites = [], isLoading, isFetching, refetch } =
    useAffiliateProgramInvites({
      programId,
      search: search || null,
      status: status === 'all' ? null : status,
    });

  // Query paralela SEM filtro de status → alimenta os KPIs para que os totais
  // sejam consistentes independente do filtro aplicado (mesmo padrão usado em
  // ReferralAffiliates). TanStack Query cacheia por queryKey, sem custo extra
  // quando `status = all`.
  const { data: invitesForKpis = [] } = useAffiliateProgramInvites({
    programId,
    search: search || null,
    status: null,
  });

  const createMut = useCreateAffiliateProgramInvite();
  const revokeMut = useRevokeAffiliateProgramInvite(programId);
  const resendMut = useResendAffiliateProgramInvite(programId);

  const [form, setForm] = useState({ email: '', name: '', phone: '' });

  const kpis = useMemo(() => {
    const total = invitesForKpis[0]?.total_count ?? invitesForKpis.length;
    let pending = 0, accepted = 0, revokedOrExpired = 0;
    for (const inv of invitesForKpis) {
      if (inv.status === 'pending') pending++;
      else if (inv.status === 'accepted') accepted++;
      else if (inv.status === 'revoked' || inv.status === 'expired') revokedOrExpired++;
    }
    return { total, pending, accepted, revokedOrExpired };
  }, [invitesForKpis]);

  const handleCreate = async () => {
    if (!form.email.trim()) {
      toast({ title: 'Informe o e-mail', variant: 'destructive' });
      return;
    }
    try {
      const result = await createMut.mutateAsync({
        programId,
        email: form.email.trim(),
        name: form.name.trim() || null,
        phone: form.phone.trim() || null,
      });
      if (result.already_member) {
        toast({ title: 'Este e-mail já participa do programa' });
      } else if (result.auto_linked) {
        toast({ title: 'Afiliado vinculado ao programa automaticamente' });
      } else {
        toast({ title: 'Convite enviado com sucesso' });
      }
      setForm({ email: '', name: '', phone: '' });
      setDialogOpen(false);
    } catch (err) {
      toast({
        title: 'Falha ao criar convite',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  };

  const handleResend = async (id: string) => {
    try {
      await resendMut.mutateAsync(id);
      toast({ title: 'Convite reenviado' });
    } catch (err) {
      toast({
        title: 'Falha ao reenviar',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  };

  const handleRevoke = async (id: string) => {
    if (!confirm('Revogar este convite?')) return;
    try {
      await revokeMut.mutateAsync(id);
      toast({ title: 'Convite revogado' });
    } catch (err) {
      toast({
        title: 'Falha ao revogar',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  };

  const total = invites[0]?.total_count ?? invites.length;

  return (
    <ListPageLayout>
      {/* Ações à direita (padrão embedded — sem header próprio) */}
      <div className="flex justify-end gap-2 px-4 pt-3">
        <Button
          variant="outline"
          size="icon-sm"
          onClick={() => refetch()}
          title="Atualizar"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
        <Button size="xs" onClick={() => setDialogOpen(true)}>
          <Plus className="h-3.5 w-3.5" />
          Novo convite
        </Button>
      </div>

      {/* KPIs padronizados */}
      <ListPageKpis>
        <KpiCard color="purple" icon={Mail}         label="Total"     value={kpis.total} />
        <KpiCard color="yellow" icon={Clock}        label="Pendentes" value={kpis.pending} />
        <KpiCard color="green"  icon={CheckCircle2} label="Aceitos"   value={kpis.accepted} />
        <KpiCard color="red"    icon={XCircle}      label="Revogados / Expirados" value={kpis.revokedOrExpired} />
      </ListPageKpis>

      {/* Filtros rápidos */}
      <ListPageFiltersBar isFetching={isFetching}>
        <div className="relative">
          <Search className="h-3 w-3 sm:h-3.5 sm:w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Buscar por e-mail ou nome…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={cn(LIST_FILTERS_INPUT_CLASS, 'pl-7 w-[160px] sm:w-[260px]')}
          />
        </div>

        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[150px]')}>
            <CircleDot className="h-3 w-3 mr-1 inline-block" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Pendentes</SelectItem>
            <SelectItem value="accepted">Aceitos</SelectItem>
            <SelectItem value="expired">Expirados</SelectItem>
            <SelectItem value="revoked">Revogados</SelectItem>
          </SelectContent>
        </Select>
      </ListPageFiltersBar>

      {/* Conteúdo (tabela) */}
      <ListPageContent>
        {isLoading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
            Carregando convites…
          </div>
        ) : invites.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            <Mail className="h-8 w-8 mx-auto mb-2 opacity-40" />
            Nenhum convite {status !== 'all' ? `com status "${status}"` : 'ainda'}.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>E-mail</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Envios</TableHead>
                <TableHead>Expira em</TableHead>
                <TableHead>Criado em</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invites.map((inv) => {
                const meta = STATUS_META[inv.status];
                const Icon = meta.icon;
                return (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">{inv.email}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {inv.name ?? '—'}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
                        <Icon className={cn('h-3.5 w-3.5', meta.iconClass)} />
                        {meta.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {inv.sent_count}
                      {inv.last_sent_at && (
                        <span className="text-xs block">
                          {format(new Date(inv.last_sent_at), 'dd/MM HH:mm', { locale: ptBR })}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(inv.expires_at), 'dd/MM/yyyy', { locale: ptBR })}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(inv.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                    </TableCell>
                    <TableCell className="text-right">
                      {inv.status === 'pending' && (
                        <div className="flex items-center gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleResend(inv.id)}
                            disabled={resendMut.isPending}
                            title="Reenviar e-mail"
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRevoke(inv.id)}
                            disabled={revokeMut.isPending}
                            title="Revogar convite"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </ListPageContent>

      {/* Rodapé com totalizador */}
      {total > 0 && (
        <ListPageFooter>
          <p className="text-xs text-muted-foreground text-right">
            {invites.length} de {total} convites
          </p>
        </ListPageFooter>
      )}

      {/* Dialog: novo convite (padrão do modal de anotações do chat) */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg w-[95vw] flex flex-col p-0 gap-0 overflow-hidden">
          {/* Capa premium padronizada */}
          <DialogHeader className="px-4 sm:px-6 py-3 sm:py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
            <div className="flex items-start gap-3 min-w-0 pr-8">
              <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg shrink-0">
                <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              </div>
              <div className="flex flex-col min-w-0">
                <DialogTitle className="truncate">Novo convite</DialogTitle>
                <p className="text-xs text-muted-foreground line-clamp-2 sm:line-clamp-none">
                  Envie um convite por e-mail para vincular a pessoa a este programa.
                </p>
              </div>
            </div>
          </DialogHeader>

          {/* Corpo */}
          <div className="flex-1 overflow-auto px-6 py-5 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="inv-email">E-mail *</Label>
              <Input
                id="inv-email"
                type="email"
                placeholder="pessoa@exemplo.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                maxLength={255}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-name">Nome (opcional)</Label>
              <Input
                id="inv-name"
                placeholder="Nome do convidado"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                maxLength={120}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-phone">Telefone (opcional)</Label>
              <Input
                id="inv-phone"
                placeholder="(11) 99999-9999"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                maxLength={20}
              />
            </div>
            <InlineNotice variant="info">
              O convite expira em 7 dias. Se o e-mail já for afiliado do tenant,
              a pessoa será vinculada automaticamente ao programa.
            </InlineNotice>
          </div>

          {/* Rodapé */}
          <DialogFooter className="px-6 py-4 border-t bg-muted/30">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={createMut.isPending}>
              {createMut.isPending && (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              )}
              Enviar convite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ListPageLayout>
  );
}
